import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, RotateCcw, Search, Shield, Trash2, UserCheck } from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import { useUserRole, UserRole } from '../hooks/useUserRole';
import {
  ADMIN_FEATURES,
  FeatureKey,
  UserFeatureState,
  defaultFeatureEnabled,
} from '../lib/adminFeatures';
import {
  DEFAULT_ADMIN_SORT,
  ProfileSortKey,
  SortState,
  nextSortState,
  sortProfiles,
} from '../lib/adminTableSort';
import SortableTh from '../components/admin/SortableTh';
import { deleteUser } from '../lib/services/AdminService';

type Profile = {
  id: string;
  email: string;
  nom?: string;
  prenom?: string;
  role?: UserRole;
  created_at?: string;
  last_sign_in_at?: string;
};

type Toast = { type: 'success' | 'error'; message: string };

const AdminUsers: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [expandedUserId, setExpandedUserId] = useState<string | null>(null);
  const [userFeatures, setUserFeatures] = useState<Record<string, UserFeatureState[]>>({});
  const [featuresLoading, setFeaturesLoading] = useState<string | null>(null);
  const [updatingRoleUserId, setUpdatingRoleUserId] = useState<string | null>(null);
  const [updatingFeatureKey, setUpdatingFeatureKey] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [sort, setSort] = useState<SortState>(DEFAULT_ADMIN_SORT);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!roleLoading && (!user || !isAdmin)) {
      navigate('/');
    }
  }, [user, isAdmin, roleLoading, navigate]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: rpcError } = await supabase.rpc('admin_get_all_profiles');
      if (rpcError) throw rpcError;

      const loaded = ((data as any[]) || []).map((p) => ({
        id: p.id,
        email: p.email,
        nom: p.nom,
        prenom: p.prenom,
        role: (p.role || 'free') as UserRole,
        created_at: p.created_at,
        last_sign_in_at: p.last_sign_in_at,
      }));

      setProfiles(loaded);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erreur lors du chargement des utilisateurs';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      fetchProfiles();
    }
  }, [isAdmin, fetchProfiles]);

  const filtered = useMemo(() => {
    const list = !search.trim()
      ? profiles
      : profiles.filter((p) => {
          const q = search.toLowerCase();
          return (
            p.email?.toLowerCase().includes(q) ||
            p.nom?.toLowerCase().includes(q) ||
            p.prenom?.toLowerCase().includes(q)
          );
        });
    return sortProfiles(list as Record<string, unknown>[], sort) as Profile[];
  }, [profiles, search, sort]);

  const handleSort = (key: ProfileSortKey) => {
    setSort((prev) => nextSortState(prev, key));
  };

  const handleDeleteUser = async (profile: Profile) => {
    if (profile.role === 'admin') {
      setToast({ type: 'error', message: 'Impossible de supprimer un compte administrateur' });
      return;
    }
    if (profile.id === user?.id) {
      setToast({ type: 'error', message: 'Impossible de supprimer votre propre compte' });
      return;
    }

    const warn =
      `Supprimer définitivement ${profile.email} ?\n\n` +
      `Cette action efface le compte et toutes ses données SuiviFinance, ` +
      `ainsi que ses données sur les autres apps (travaux, mariage, brocante, etc.).\n\n` +
      `Cette action est irréversible.`;
    if (!window.confirm(warn)) return;

    const confirmEmail = window.prompt(
      `Pour confirmer, saisissez l'email exact de l'utilisateur :\n${profile.email}`,
    );
    if (confirmEmail?.trim().toLowerCase() !== profile.email.trim().toLowerCase()) {
      setToast({ type: 'error', message: 'Suppression annulée (email non confirmé)' });
      return;
    }

    setDeletingUserId(profile.id);
    setToast(null);
    try {
      const { data, error: delError } = await deleteUser(profile.id);
      if (delError || !data?.success) {
        const message =
          (typeof delError === 'object' && delError && 'message' in delError
            ? String((delError as { message?: string }).message)
            : null) ||
          (typeof delError === 'string' ? delError : null) ||
          data?.error ||
          'Erreur lors de la suppression';
        setToast({ type: 'error', message });
        return;
      }

      // Ne retirer de la liste que si auth.users a bien été supprimé
      const authDeleted = Number(data.deleted_counts?.auth_users ?? 0) > 0;
      if (!authDeleted) {
        setToast({
          type: 'error',
          message:
            data.error ||
            'Suppression incomplète : le compte auth est toujours présent. Réessayez.',
        });
        await fetchProfiles();
        return;
      }

      setProfiles((prev) => prev.filter((p) => p.id !== profile.id));
      if (expandedUserId === profile.id) setExpandedUserId(null);
      setToast({
        type: 'success',
        message: data.message || `Utilisateur ${profile.email} supprimé`,
      });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erreur lors de la suppression';
      setToast({ type: 'error', message });
    } finally {
      setDeletingUserId(null);
    }
  };

  const loadUserFeatures = async (userId: string) => {
    setFeaturesLoading(userId);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_get_user_features', {
        p_user_id: userId,
      });
      if (rpcError) throw rpcError;

      const features = ((data as { features?: UserFeatureState[] })?.features || []) as UserFeatureState[];
      setUserFeatures((prev) => ({ ...prev, [userId]: features }));
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Impossible de charger les fonctionnalités';
      setToast({ type: 'error', message });
    } finally {
      setFeaturesLoading(null);
    }
  };

  const toggleExpand = async (userId: string) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }
    setExpandedUserId(userId);
    if (!userFeatures[userId]) {
      await loadUserFeatures(userId);
    }
  };

  const handleRoleChange = async (profile: Profile, newRole: UserRole) => {
    if (profile.role === newRole) return;

    setUpdatingRoleUserId(profile.id);
    setToast(null);
    try {
      const { error: rpcError } = await supabase.rpc('admin_update_user_role', {
        p_user_id: profile.id,
        p_role: newRole,
      });
      if (rpcError) throw rpcError;

      setProfiles((prev) =>
        prev.map((p) => (p.id === profile.id ? { ...p, role: newRole } : p)),
      );

      if (expandedUserId === profile.id) {
        await loadUserFeatures(profile.id);
      }

      setToast({ type: 'success', message: `Rôle de ${profile.email} : ${newRole}` });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erreur lors de la mise à jour du rôle';
      setToast({ type: 'error', message });
    } finally {
      setUpdatingRoleUserId(null);
    }
  };

  const handleFeatureToggle = async (userId: string, featureKey: FeatureKey, enabled: boolean) => {
    setUpdatingFeatureKey(`${userId}:${featureKey}`);
    setToast(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_set_user_feature', {
        p_user_id: userId,
        p_feature_key: featureKey,
        p_enabled: enabled,
      });
      if (rpcError) throw rpcError;

      const features = ((data as { features?: UserFeatureState[] })?.features || []) as UserFeatureState[];
      setUserFeatures((prev) => ({ ...prev, [userId]: features }));
      setToast({ type: 'success', message: 'Fonctionnalité mise à jour' });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erreur lors de la mise à jour';
      setToast({ type: 'error', message });
    } finally {
      setUpdatingFeatureKey(null);
    }
  };

  const handleResetFeatures = async (userId: string, email: string) => {
    if (!window.confirm(`Réinitialiser les fonctionnalités de ${email} aux valeurs par défaut du rôle ?`)) {
      return;
    }

    setFeaturesLoading(userId);
    setToast(null);
    try {
      const { data, error: rpcError } = await supabase.rpc('admin_reset_user_features', {
        p_user_id: userId,
      });
      if (rpcError) throw rpcError;

      const features = ((data as { features?: UserFeatureState[] })?.features || []) as UserFeatureState[];
      setUserFeatures((prev) => ({ ...prev, [userId]: features }));
      setToast({ type: 'success', message: 'Fonctionnalités réinitialisées' });
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Erreur lors de la réinitialisation';
      setToast({ type: 'error', message });
    } finally {
      setFeaturesLoading(null);
    }
  };

  const renderFeaturePanel = (profile: Profile) => {
    const features = userFeatures[profile.id];
    const role = profile.role || 'free';
    const isLoadingFeatures = featuresLoading === profile.id;

    if (isLoadingFeatures && !features) {
      return (
        <td colSpan={8} className="px-4 py-4 bg-dark-900/50">
          <div className="text-dark-400 text-sm">Chargement des fonctionnalités…</div>
        </td>
      );
    }

    const effectiveFeatures: UserFeatureState[] =
      features ||
      ADMIN_FEATURES.map((f) => ({
        feature_key: f.key,
        enabled: defaultFeatureEnabled(role, f.key),
        is_override: false,
        role_default: defaultFeatureEnabled(role, f.key),
      }));

    const hasOverrides = effectiveFeatures.some((f) => f.is_override);

    return (
      <td colSpan={8} className="px-4 py-4 bg-dark-900/50">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <p className="text-sm text-dark-300">
              Fonctionnalités pour <span className="text-white">{profile.email}</span>
              {' '}(rôle : <span className="text-primary-300">{role}</span>)
            </p>
            {hasOverrides && (
              <button
                onClick={() => handleResetFeatures(profile.id, profile.email)}
                className="inline-flex items-center gap-1.5 text-xs text-dark-400 hover:text-white px-2 py-1 rounded border border-dark-700 hover:border-dark-600"
              >
                <RotateCcw className="w-3.5 h-3.5" />
                Réinitialiser au rôle
              </button>
            )}
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            {ADMIN_FEATURES.map((def) => {
              const state = effectiveFeatures.find((f) => f.feature_key === def.key);
              const enabled = state?.enabled ?? defaultFeatureEnabled(role, def.key);
              const isOverride = state?.is_override ?? false;
              const busy = updatingFeatureKey === `${profile.id}:${def.key}`;

              return (
                <label
                  key={def.key}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    enabled
                      ? 'border-primary-700/40 bg-primary-900/10'
                      : 'border-dark-700 bg-dark-800/50'
                  } ${busy ? 'opacity-60' : 'hover:border-dark-600'}`}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={busy || isLoadingFeatures}
                    onChange={(e) => handleFeatureToggle(profile.id, def.key, e.target.checked)}
                    className="mt-0.5 rounded border-dark-600 bg-dark-800 text-primary-500 focus:ring-primary-500"
                  />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-white">{def.label}</span>
                      {isOverride && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-200 border border-amber-800">
                          perso
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-dark-400 mt-0.5">{def.description}</p>
                    <p className="text-[11px] text-dark-500 mt-1">
                      Défaut rôle : {state?.role_default ? 'activé' : 'désactivé'}
                    </p>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      </td>
    );
  };

  if (roleLoading) {
    return (
      <div className="py-12 text-center text-dark-400">Chargement…</div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-dark-300 hover:text-white inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour
          </button>
          <h1 className="text-3xl font-bold text-white mt-2 inline-flex items-center gap-2">
            <Shield className="w-7 h-7 text-primary-400" />
            Administration
          </h1>
          <p className="text-dark-400 mt-1">
            Gestion des rôles et des fonctionnalités par utilisateur.
          </p>
        </div>

        <button
          onClick={() => navigate('/admin/impersonate')}
          className="inline-flex items-center gap-2 px-3 py-2 bg-dark-800 border border-dark-700 text-dark-200 rounded-md hover:bg-dark-700 hover:text-white shrink-0"
        >
          <UserCheck className="w-4 h-4" />
          Impersonation (debug)
        </button>
      </div>

      {toast && (
        <div
          className={`px-4 py-3 rounded-lg border text-sm ${
            toast.type === 'success'
              ? 'bg-green-900/30 border-green-700 text-green-200'
              : 'bg-red-900/30 border-red-700 text-red-200'
          }`}
        >
          {toast.message}
        </div>
      )}

      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-dark-400 w-5 h-5" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher par email, nom, prénom"
          className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-dark-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
        />
      </div>

      <div className="bg-dark-960 border border-dark-800 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-dark-900 text-dark-300">
            <tr>
              <th className="w-8 px-2 py-3" />
              <SortableTh
                label="Email"
                sortKey="email"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Nom"
                sortKey="nom"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Prénom"
                sortKey="prenom"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Rôle"
                sortKey="role"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Créé le"
                sortKey="created_at"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <SortableTh
                label="Dernière connexion"
                sortKey="last_sign_in_at"
                activeKey={sort.key}
                dir={sort.dir}
                onSort={handleSort}
              />
              <th className="text-right px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-dark-400">
                  Chargement…
                </td>
              </tr>
            )}
            {!loading && error && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-red-400">
                  {error}
                </td>
              </tr>
            )}
            {!loading && !error && filtered.length === 0 && (
              <tr>
                <td colSpan={8} className="px-4 py-6 text-center text-dark-400">
                  Aucun utilisateur
                </td>
              </tr>
            )}
            {!loading &&
              !error &&
              filtered.map((p) => (
                <React.Fragment key={p.id}>
                  <tr className="border-t border-dark-800 hover:bg-dark-900/40">
                    <td className="px-2 py-3 text-center">
                      <button
                        onClick={() => toggleExpand(p.id)}
                        className="p-1 text-dark-400 hover:text-white rounded"
                        title="Fonctionnalités"
                      >
                        {expandedUserId === p.id ? (
                          <ChevronDown className="w-4 h-4" />
                        ) : (
                          <ChevronRight className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-white">{p.email}</td>
                    <td className="px-4 py-3 text-dark-200">{p.nom || '-'}</td>
                    <td className="px-4 py-3 text-dark-200">{p.prenom || '-'}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <select
                          value={p.role || 'free'}
                          onChange={(e) => handleRoleChange(p, e.target.value as UserRole)}
                          disabled={updatingRoleUserId === p.id}
                          className="bg-dark-800 border border-dark-700 text-white text-sm rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          <option value="free">free</option>
                          <option value="premium">premium</option>
                          <option value="admin">admin</option>
                        </select>
                        {updatingRoleUserId === p.id && (
                          <div className="w-4 h-4 border-2 border-primary-300 border-t-transparent rounded-full animate-spin" />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-dark-400">
                      {p.created_at ? new Date(p.created_at).toLocaleString('fr-FR') : '-'}
                    </td>
                    <td className="px-4 py-3 text-dark-400">
                      {p.last_sign_in_at
                        ? new Date(p.last_sign_in_at).toLocaleString('fr-FR')
                        : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => handleDeleteUser(p)}
                        disabled={deletingUserId === p.id || p.role === 'admin' || p.id === user?.id}
                        title={
                          p.role === 'admin'
                            ? 'Compte administrateur protégé'
                            : p.id === user?.id
                              ? 'Impossible de supprimer votre compte'
                              : 'Supprimer l’utilisateur et toutes ses données'
                        }
                        className="inline-flex items-center justify-center p-2 rounded-md text-red-400/80 hover:text-red-300 hover:bg-red-900/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        aria-label={`Supprimer ${p.email}`}
                      >
                        {deletingUserId === p.id ? (
                          <div className="w-4 h-4 border-2 border-red-300 border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <Trash2 className="w-4 h-4" />
                        )}
                      </button>
                    </td>
                  </tr>
                  {expandedUserId === p.id && (
                    <tr className="border-t border-dark-800">{renderFeaturePanel(p)}</tr>
                  )}
                </React.Fragment>
              ))}
          </tbody>
        </table>
      </div>

      <p className="text-xs text-dark-500">
        Les fonctionnalités sans badge « perso » suivent le rôle. Une case cochée manuellement crée une
        surcharge individuelle. Utilisez « Réinitialiser au rôle » pour revenir aux défauts.
      </p>
    </div>
  );
};

export default AdminUsers;
